import React from 'react';
import { CloudUpload, Trash2, CheckCircle, FileText } from 'lucide-react';
import PropTypes from 'prop-types';

const UplodeView = ({
  uploadedFile,
  uploadProgress,
  handleDeleteFile,
  dragging,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  ensureAuthenticated,
  state,
  isFullyAuthenticated,
  fileInputRef,
  handleFileInputChange,
  SUPPORTED_FORMATS,
  selectedLanguage,
  setSelectedLanguage,
  selectedType,
  setSelectedType,
  toast,
}) => (
  <div className="flex flex-col justify-center items-center from-pl_bg_light_2 to-pl_bg_light_3 p-2 space-y-6 h-full">
    <div className="w-full  max-w-6xl">
      {uploadedFile ? (
        <div className="transform transition-all duration-300 hover:scale-[1.02]">
          <div className="flex items-center justify-between bg-white/90 backdrop-blur-sm p-1 rounded-lg shadow-md border border-pl_txt_light_3 min-h-[80px]">
            <div className="flex items-center space-x-3">
              <div className="bg-pl_bg_light_3 p-1.5 rounded-md">
                <FileText className="text-pl_prim_color_1" size={20} />
              </div>
              <span
                className="text-pl_txt_light_2 font-semibold text-sm truncate max-w-[200px] sm:max-w-[300px] md:max-w-[400px]"
                title={uploadedFile.name}
              >
                {uploadedFile.name}
              </span>
            </div>
            <button
              onClick={handleDeleteFile}
              className="text-red-500 hover:text-red-400 transition-colors duration-200 p-1.5 rounded-full hover:bg-red-50"
            >
              <Trash2 size={18} />
            </button>
          </div>
          {uploadProgress === 'loading' ? (
            <div className="mt-3 text-center">
              <p className="text-sm text-pl_txt_light_1 flex items-center justify-center">
                Uploading...
                <svg
                  className="animate-spin h-4 w-4 ml-1 text-pl_prim_color_1"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v2a6 6 0 00-6 6H4z"
                  ></path>
                </svg>
              </p>
            </div>
          ) : uploadProgress === 'success' ? (
            <div className="mt-3 text-center">
              <p className="text-sm text-pl_txt_light_1 flex items-center justify-center">
                <CheckCircle className="text-green-500 mr-1.5" size={18} />
                <span className="text-pl_prim_color_1">Checks cleared</span>
              </p>
            </div>
          ) : null}
        </div>
      ) : (
        <div
          className={`w-full border-2 border-dashed rounded-lg p-4 text-center transition-all duration-300 flex flex-col items-center justify-center bg-white/90 backdrop-blur-sm shadow-md hover:shadow-lg ${dragging ? 'border-pl_prim_color_1 bg-pl_bg_light_4 scale-[1.02]' : 'border-pl_txt_light_3 hover:border-pl_prim_color_1'}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={ensureAuthenticated}
        >
          <div className="bg-pl_bg_light_3 p-3 rounded-full  shadow-inner">
            <CloudUpload className={`${state.isAuthenticated && isFullyAuthenticated ? 'text-pl_prim_color_1' : 'text-pl_txt_light_2 opacity-80'}`} size={36} />
          </div>
          <p className={`text-xl font-semibold  ${state.isAuthenticated && isFullyAuthenticated ? 'text-pl_prim_color_1' : 'text-pl_txt_light_2 opacity-80'}`}>
            Drag & drop your file here
          </p>
          <p className="text-pl_txt_light_3 text-sm ">or</p>
          <label
            className={`inline-block px-8 py-2 rounded-full transition-all duration-300 ${state.isAuthenticated && isFullyAuthenticated ? 'bg-pl_prim_color_1 text-white cursor-pointer hover:bg-pl_prim_color_2 hover:shadow-md transform hover:-translate-y-0.5' : 'bg-pl_bg_light_2 text-pl_txt_light_2 cursor-pointer opacity-80'} font-bold text-sm mb-4 shadow-sm`}
            onClick={(e) => {
              e.preventDefault();
              if (!state.isAuthenticated) {
                ensureAuthenticated();
              } else if (!isFullyAuthenticated) {
                // toast.info('Finalizing authentication, please wait a moment...');
              } else {
                if (fileInputRef.current) {
                  fileInputRef.current.value = null;
                  setTimeout(() => {
                    fileInputRef.current.click();
                  }, 0);
                }
              }
            }}
          >
            Browse files
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={handleFileInputChange}
              onClick={(e) => e.stopPropagation()}
              disabled={!state.isAuthenticated}
            />
          </label>
          <p className="text-center text-pl_txt_light_3 text-xs">
            Supported formats:{' '}
            <span className="text-pl_txt_light_4 font-medium">
              PDF, DOCX, DOC, JPG, JPEG, PNG
            </span>
          </p>
        </div>
      )}
    </div>

    <div className="w-full max-w-6xl">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="p-0">
          <label className="text-base text-pl_txt_light_2 mb-3 font-semibold flex items-center">
            <div className="bg-pl_bg_light_3 p-1.5 rounded-md mr-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-languages-icon lucide-languages text-pl_prim_color_1 w-6 h-6"
              >
                <path d="m5 8 6 6" />
                <path d="m4 14 6-6 2-3" />
                <path d="M2 5h12" />
                <path d="M7 2h1" />
                <path d="m22 22-5-10-5 10" />
                <path d="M14 18h6" />
              </svg>
            </div>
            Language
          </label>
          <select
            value={selectedLanguage}
            onChange={(e) => setSelectedLanguage(e.target.value)}
            className="w-full p-3 border border-pl_txt_light_3 rounded-md focus:outline-none focus:border-pl_prim_color_1 focus:ring-1 focus:ring-pl_prim_color_1/20 bg-white transition-all duration-200 text-sm"
          >
            <option value="">Select language</option>
            <option value="ta_en">Tamil to English</option>
            <option value="si_en">Sinhala to English</option>
            <option value="ta_si">Tamil to Sinhala</option>
            <option value="en_si">English to Sinhala</option>
            <option value="en_ta">English to Tamil</option>
            <option value="si_ta">Sinhala to Tamil</option>
          </select>
        </div>
        <div className="p-0">
          <label className="text-base text-pl_txt_light_2 mb-3 font-semibold flex items-center">
            <div className="bg-pl_bg_light_3 p-1.5 rounded-md mr-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className="lucide lucide-bot-icon lucide-bot text-pl_prim_color_1 w-6 h-6"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 8V4H8" />
                <rect width="16" height="12" x="4" y="8" rx="2" />
                <path d="M2 14h2" />
                <path d="M20 14h2" />
                <path d="M15 13v2" />
                <path d="M9 13v2" />
              </svg>
            </div>
            Translation type
          </label>
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="w-full p-3 border border-pl_txt_light_3 rounded-md focus:outline-none focus:border-pl_prim_color_1 focus:ring-1 focus:ring-pl_prim_color_1/20 bg-white transition-all duration-200 text-sm"
          >
            <option value="">Select Type</option>
            <option value="machine">Machine translation</option>
          </select>
        </div>
      </div>
    </div>
  </div>
);

UplodeView.propTypes = {
  uploadedFile: PropTypes.shape({
    name: PropTypes.string,
  }),
  uploadProgress: PropTypes.string,
  handleDeleteFile: PropTypes.func.isRequired,
  dragging: PropTypes.bool,
  handleDragOver: PropTypes.func.isRequired,
  handleDragLeave: PropTypes.func.isRequired,
  handleDrop: PropTypes.func.isRequired,
  ensureAuthenticated: PropTypes.func.isRequired,
  state: PropTypes.shape({
    isAuthenticated: PropTypes.bool,
  }).isRequired,
  isFullyAuthenticated: PropTypes.bool,
  fileInputRef: PropTypes.shape({ current: PropTypes.any }),
  handleFileInputChange: PropTypes.func.isRequired,
  SUPPORTED_FORMATS: PropTypes.arrayOf(PropTypes.string),
  selectedLanguage: PropTypes.string,
  setSelectedLanguage: PropTypes.func.isRequired,
  selectedType: PropTypes.string,
  setSelectedType: PropTypes.func.isRequired,
  toast: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({
      info: PropTypes.func,
    })
  ]),
};

export default UplodeView;
